import type { KnowledgeUnit } from '../types'

// ---------------------------------------------------------------------------
// concepts.ts — curated concept units
//
// Draft revision v0 (not yet analyst-greenlit per spec §18).
// Metric definitions are plausible approximations; INFERRED entries are marked.
// Exact formulas / value_type semantics to be validated in the post-build tuning
// pass by the analyst.
// ---------------------------------------------------------------------------

export const concepts: KnowledgeUnit[] = [
  {
    id: 'concept:npv',
    type: 'concept',
    keys: ['npv', 'net present value', 'value_type', 'kpi encoding'],
    version: '0.1-draft',
    title: 'NPV (value_type)',
    summary:
      'The net-present-value variant of a KPI; the third segment in the v3 KPI encoding string.',
    body: `NPV is a value_type selector in the v3 KPI encoding convention.

KPI encoding format: \`<kpi_root>__<driver>__<value_type>\`
Example: \`sales_roi__nsp__npv\` = Sales RoI KPI, driven by NSP spend, expressed as the NPV value type.

What npv means in this context (INFERRED — confirm with analyst):
The "npv" value_type selects the discounted / present-value representation of the KPI, as opposed to e.g. the raw or index variant. In the Campaign Performance and Dynamics pages, npv is the default value_type for sales_roi — it reflects the model's estimate of the net economic value delivered per unit of spend, expressed in present-value terms.

Distinct from "net spend" (nsp) — npv is a value_type, not a driver or spend metric.

Cross-references: kpi:sales_roi uses npv as its default value_type.`,
    seeAlso: ['kpi:sales_roi'],
  },

  {
    id: 'concept:mediashare',
    type: 'concept',
    keys: ['mediashare', 'media share', 'media contribution', 'ratio', 'decomposition'],
    version: '0.1-draft',
    title: 'Mediashare',
    summary:
      'Ratio KPI: the share of total actual sales attributable to media spend (INFERRED formula).',
    body: `Mediashare (encoded root: sales_mediashare) is one of the four ratio KPI roots in MMMv3 (alongside sales_roi, sales_cpo, sales_opc).

INFERRED formula: mediashare = media-attributed sales ÷ total actual sales.
Expressed as a percentage or fraction; higher = media is driving a larger share of total sales.

How it is used (INFERRED):
- Typical value_type: percentage (proportion).
- Useful for understanding the structural role of media relative to baseline / non-media sales.
- Can be filtered and grouped by channel/dimension just like other ratio KPIs.

Pages: not a lead KPI on any page by default, but can be selected on Campaign Performance, Dynamics, and MultiDeepDive (all share the model_tracking fact table which supports ratio walks).

DRAFT — exact formula and walk semantics to be confirmed with the analyst during the post-build tuning pass (spec §18).`,
    seeAlso: ['kpi:sales'],
  },

  {
    id: 'concept:cpo',
    type: 'concept',
    keys: ['cpo', 'cost per order', 'cost per acquisition', 'efficiency', 'ratio'],
    version: '0.1-draft',
    title: 'CPO (Cost Per Order)',
    summary: 'Ratio KPI: net spend divided by orders; lower is more efficient (INFERRED formula).',
    body: `CPO (Cost Per Order; encoded root: sales_cpo) is one of the four ratio KPI roots in MMMv3 (alongside sales_roi, sales_mediashare, sales_opc).

INFERRED formula: cpo = net spend (nsp) ÷ orders.
Lower CPO = more efficient (same orders for less spend, or more orders for the same spend).
Units: currency per order (e.g. EUR/order).

How it is used (INFERRED):
- The denominator is "orders" — a count metric; the numerator is net spend.
- Useful for evaluating absolute acquisition cost across campaigns, channels, or time periods.
- Not the same as sales_roi (which is return on spend, dimensioned as value per spend unit).

Pages: can be selected on Campaign Performance, Dynamics, and MultiDeepDive when campaigns/channels are being evaluated on cost efficiency rather than revenue return.

DRAFT — exact formula and eligible drivers/value_types to be confirmed with the analyst during the post-build tuning pass (spec §18).`,
    seeAlso: ['kpi:nsp'],
  },

  {
    id: 'concept:opc',
    type: 'concept',
    keys: ['opc', 'orders per cost', 'orders per spend', 'efficiency', 'ratio', 'cpo inverse'],
    version: '0.1-draft',
    title: 'OpC (Orders Per Cost)',
    summary:
      'Ratio KPI: orders divided by net spend — the CpO inverse; higher is more efficient (INFERRED formula).',
    body: `OpC (Orders Per Cost; encoded root: sales_opc) is one of the four ratio KPI roots in MMMv3 (alongside sales_roi, sales_cpo, sales_mediashare).

INFERRED formula: opc = orders ÷ net spend (nsp) — the arithmetic inverse of CpO (sales_cpo).
Higher OpC = more efficient (more orders per EUR of spend). Units: orders per currency (e.g. orders/EUR).

v3 KPI encoding: \`sales_opc__nsp__units\`
  - root = sales_opc
  - driver = nsp (net spend is the denominator)
  - value_type = units

How it is used (INFERRED):
- The numerator is "orders" (a count metric); the denominator is net spend — the reciprocal framing of CpO.
- Read it as the mirror image of CpO: where CpO answers "how much did each order cost?", OpC answers "how many orders did each EUR of spend buy?".
- Not the same as sales_roi (return on spend, dimensioned as sales value per spend unit).

Pages: selectable on Campaign Performance, Dynamics, and MultiDeepDive alongside the other ratio KPIs (walk_ratio path on the model_tracking fact table).

DRAFT — exact formula and eligible drivers/value_types to be confirmed with the analyst during the post-build tuning pass (spec §18).`,
    seeAlso: ['concept:cpo', 'kpi:nsp'],
  },

  {
    id: 'concept:data-boundaries',
    type: 'concept',
    keys: [
      'data boundary',
      'not available',
      'cannot answer',
      'missing dimension',
      'no data for',
      'gap',
      'limitation',
      'decomposition',
      'breakdown not possible',
      'granularity',
    ],
    version: '0.1-draft',
    title: 'Data boundaries — what the facts carry, and steering around gaps',
    summary:
      'Each fact table carries a specific set of dimensions and value types; when a question needs an axis the facts lack, name the boundary and offer the nearest answerable cut.',
    body: `The model's fact tables each carry a specific set of dimensions and value types — the Dataset Overview in your system context lists them, and describe_page returns the complete live values. A question is answerable only when some fact carries every axis it needs.

When a question needs an axis no fact carries:

- Check first — consult the Dataset Overview (and describe_page for full dim lists) before declaring a gap; the data may have gained the axis since this knowledge was written.
- Name the boundary honestly — say which decomposition the data does not carry. Never approximate the missing cut from figures you do have, and never present a proxy as the real thing.
- Offer the nearest answerable cut — the closest question the facts CAN answer (a coarser grouping, a total instead of a split, a time-window comparison instead of a dimension split), clearly labelled as such, and answer it with live query_kpi figures if the user agrees.

Naming a boundary precisely reads as competence; papering over it with a wrong number destroys trust.`,
    seeAlso: [],
  },
]
